import { useState, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import MediaPlayer from "@/components/ui/media-player.tsx";
import ExerciseCompletion from "@/components/ui/exercise-completion";
import { breathingData, getActiveSegment } from "@/lib/data/breathing";
import BreathingPrompts from "./BreathingPrompts";
import CountdownComponent from "./CountdownComponent";
import { PageModal } from "@/components/exercise-modals/PageModal";
import ConfirmationModal from "@/components/ui/confirmation-modal";
import SegmentDataDisplay from "@/components/Exercises/Body-Exercise/SegmentDataDisplay";
import { Overlay } from "@/components/ui/overlay";

interface BreathingExerciseContentProps {
  onClose: () => void;
  isShow?: boolean;
  onExit?: () => void;
}

export default function BreathingExerciseContent({
  onClose,
  isShow = false,
  onExit,
}: BreathingExerciseContentProps) {

  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [isLoading] = useState(false);
  const [countdownComplete, setCountdownComplete] = useState(false);
  const [controlsDisabled, setControlsDisabled] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [shouldStopAudio, setShouldStopAudio] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isManualNavigation, setIsManualNavigation] = useState(false);

  const totalSegments = breathingData.length;
  const currentSegment = breathingData[currentSegmentIndex];

  const handleTimeUpdate = useCallback(
    (time: number) => {
      if (isManualNavigation) {
        setIsManualNavigation(false);
        return;
      }

      const activeIndex = getActiveSegment(time);
      if (activeIndex !== currentSegmentIndex) {
        setCurrentSegmentIndex(activeIndex);
        setCountdownComplete(false);
        setControlsDisabled(false);
      }
    },
    [currentSegmentIndex, isManualNavigation]
  );

  const handlePlayStateChange = (playing: boolean, started: boolean) => {
    setIsPlaying(playing);
    setHasStartedPlaying(started);
  };

  const handleCountdownComplete = () => {
    setCountdownComplete(true);
    setIsCompleted(true);
  };

  const handleControlsDisabledChange = (disabled: boolean) => {
    setControlsDisabled(disabled);
  };

  // const handleAudioEnded = () => {
  //   const isLast = currentSegmentIndex === totalSegments - 1;
  //   if (isLast && countdownComplete) {
  //     // finishing state handled by showing ExerciseCompletion
  //   }
  // };

  const goToSegmentById = (id: number) => {
    const idx = Math.max(
      0,
      Math.min(
        totalSegments - 1,
        breathingData.findIndex((s) => s.id === id)
      )
    );
    setCurrentSegmentIndex(idx === -1 ? 0 : idx);
    setIsPlaying(false);
    setHasStartedPlaying(false);
    setCountdownComplete(false);
    setIsCompleted(false);
  };

  const handleFinish = () => {
    // Reset all states when finishing
    setCurrentSegmentIndex(0);
    setIsPlaying(false);
    setHasStartedPlaying(false);
    setCountdownComplete(false);
    setIsCompleted(false);
    setControlsDisabled(false);
    setShouldStopAudio(false);
    onClose();
  };

  const handleBack = () => {
    if (currentSegmentIndex > 0) {
      setIsManualNavigation(true);
      setShouldStopAudio(true);
      setIsPlaying(false);
      setCurrentSegmentIndex(currentSegmentIndex - 1);
      setCountdownComplete(false);
      setTimeout(() => {
        setShouldStopAudio(false);
      }, 100);
    }
  };

  const handleOnClose = () => {
    setShouldStopAudio(true);
    setShowExitModal(true);
  };

  const handleExitConfirm = () => {
    // Reset all states when exiting
    setCurrentSegmentIndex(0);
    setIsPlaying(false);
    setHasStartedPlaying(false);
    setCountdownComplete(false);
    setIsCompleted(false);
    setControlsDisabled(false);
    setShouldStopAudio(false);
    
    if (onExit) {
      onExit();
    } else {
      onClose();
    }
  };

  const handleExitCancel = () => {
    setShouldStopAudio(false);
    setShowExitModal(false);
  };

  const renderStepContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <p className="text-white/50">
            <FormattedMessage id="common.loading" />
          </p>
        </div>
      );
    }

    if (!currentSegment) {
      return (
        <div className="text-center py-8">
          <p className="text-white">
            <FormattedMessage id="breathing.errors.stepNotFound" />
          </p>
        </div>
      );
    }

    if (currentSegment.id === 2) {
      return (
        <BreathingPrompts
          audioSrc={currentSegment.audio}
          isPlaying={isPlaying}
          hasStartedPlaying={hasStartedPlaying}
          onControlsDisabledChange={handleControlsDisabledChange}
        />
      );
    }

    if (currentSegment.id === 7) {
      if (countdownComplete) {
        return null;
      }
      return (
        <CountdownComponent
          audioSrc={currentSegment.audio}
          isPlaying={isPlaying}
          hasStartedPlaying={hasStartedPlaying}
          onComplete={handleCountdownComplete}
          onControlsDisabledChange={handleControlsDisabledChange}
        />
      );
    }

    // For other segments, return null since content is shown by SegmentDataDisplay
    return null;
  };

  return (
    <PageModal
      isTransparent={false}
      audience="adult"
      isShow={isShow}
      onBack={currentSegmentIndex > 0 ? handleBack : undefined}
      onClose={handleOnClose}
    >
      <main className="flex flex-col justify-between max-h-full min-h-[350px]">
      <SegmentDataDisplay
        titleKey={currentSegment?.titleKey || ""}
        content={currentSegment?.content}
        segmentNumber={currentSegmentIndex + 1}
        totalSegments={totalSegments}
        isActive={isPlaying}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={`content-${currentSegment.id}`}
          className="space-y-4"
          initial={{
            opacity: 0,
            y: 16,
          }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.5,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.1,
            },
          }}
          exit={{
            opacity: 0,
            y: -12,
            transition: {
              duration: 0.3,
            },
          }}
        >
          {renderStepContent()}
        </motion.div>
      </AnimatePresence>

      <div className="relative mt-7.5">
      <AnimatePresence>
        {currentSegment?.audio &&
          !(currentSegment.id === 7 && countdownComplete) && (
            <motion.div
              initial={{
                opacity: 0,
                y: 12,
              }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.4,
                  ease: "easeOut",
                  delay: 0.2,
                },
              }}
              exit={{
                opacity: 0,
                y: -8,
                transition: { duration: 0.2 },
              }}
            >
              <MediaPlayer
                src={currentSegment.audio}
                onTimeUpdate={handleTimeUpdate}
                onPlayStateChange={handlePlayStateChange}
                // onEnded={handleAudioEnded}
                disablePlaybackControls={controlsDisabled}
                disableProgressBarSeeking={controlsDisabled}
                autoPlay={true}
                segment={currentSegment.segment}
                shouldStop={shouldStopAudio}
              />
            </motion.div>
          )}
      </AnimatePresence>
      </div>
      </main>

      <Overlay show={isCompleted} hideCloseButton={true}>
        <ExerciseCompletion
          onFinish={handleFinish}
          onRepeat={() => goToSegmentById(1)}
          completionMessageId="breathing.countdown.finished"
          finishButtonMessageId="common.finishExercise"
          repeatButtonMessageId="common.repeatExercise"
        />
      </Overlay>

      <ConfirmationModal
        isOpen={showExitModal}
        onClose={handleExitCancel}
        onConfirm={handleExitConfirm}
        titleKey="exercise.exit.title"
        messageKey="exercise.exit.message"
        confirmButtonTextKey="exercise.exit.confirm"
      />
    </PageModal>
  );
}
